import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getClientSessions, cancelSession } from '../lib/api';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Loader2, CalendarDays, MapPin, Clock, Zap, CheckCircle2, XCircle, Inbox,
  Star,
} from 'lucide-react';
import { CATEGORY_NAMES, formatCurrency, getInitials } from '../lib/utils';
import { toast } from 'sonner';
import MyPackagesPanel from '../components/MyPackagesPanel';
import ClientTrackingMap from '../components/ClientTrackingMap';

const STATUS_TONE = {
  pending_trainer_response: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  confirmed: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  declined: 'bg-red-500/15 text-red-300 border-red-500/30',
  cancelled: 'bg-zinc-700 text-zinc-300 border-zinc-600',
  completed: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
};

const STATUS_LABEL = {
  pending_trainer_response: 'Waiting on trainer',
  confirmed: 'Confirmed',
  declined: 'Declined',
  cancelled: 'Cancelled',
  completed: 'Completed',
};

const isPast = (s) => {
  try {
    const sched = new Date(`${s.scheduled_date}T${s.scheduled_time}:00`);
    return sched.getTime() < Date.now();
  } catch { return false; }
};

const SessionCard = ({ s, onCancel, onLeaveReview, busy }) => {
  const navigate = useNavigate();
  const t = s.trainer || {};
  const tp = t.trainer_profile || {};
  const status = s.status;
  const isInstant = s.booking_type === 'instant';
  const canCancel = ['pending_trainer_response', 'confirmed'].includes(status);

  return (
    <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-5" data-testid={`session-${s.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <Avatar className="w-11 h-11 border border-zinc-700">
            <AvatarImage src={tp.profile_image} />
            <AvatarFallback className="bg-[#27272A] text-[#C0A062] text-xs font-semibold">
              {getInitials(t.full_name || 'T')}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="text-white font-semibold truncate">{t.full_name}</p>
            <p className="text-xs text-zinc-500">{CATEGORY_NAMES[s.category] || s.category}</p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Badge className={STATUS_TONE[status] || 'bg-zinc-700 text-zinc-300'}>
            {STATUS_LABEL[status] || status}
          </Badge>
          {isInstant && (
            <span className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1">
              <Zap className="w-3 h-3" /> Instant
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs text-zinc-300 mb-3">
        <div className="flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5 text-[#C0A062]" />{s.scheduled_date}</div>
        <div className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-[#C0A062]" />{s.scheduled_time}</div>
        <div className="flex items-start gap-1.5 col-span-2"><MapPin className="w-3.5 h-3.5 text-[#C0A062] mt-0.5 shrink-0" /><span className="break-words">{s.location_address}</span></div>
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-zinc-800">
        <span className="text-sm text-zinc-300">{formatCurrency(s.price)}</span>
        <div className="flex gap-2 flex-wrap justify-end">
          {status === 'completed' && (
            <>
              <Button
                size="sm"
                onClick={() => onLeaveReview(s)}
                className="bg-[#C0A062] text-black hover:bg-[#a98a52]"
                data-testid={`review-${s.id}`}
              >
                <Star className="w-4 h-4 mr-1" /> Leave review
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate(`/trainers/${s.trainer_id}`)}
                className="border-[#C0A062]/40 text-[#C0A062] hover:bg-[#C0A062]/10"
                data-testid={`rebook-${s.id}`}
              >
                Book again
              </Button>
            </>
          )}
          {canCancel && (
            <Button
              size="sm"
              variant="outline"
              disabled={busy === s.id}
              onClick={() => onCancel(s)}
              className="border-zinc-700 text-zinc-200 hover:bg-zinc-800"
              data-testid={`cancel-${s.id}`}
            >
              {busy === s.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <XCircle className="w-4 h-4 mr-1" />}
              Cancel
            </Button>
          )}
        </div>
      </div>
      {status === 'completed' && s.coach_note && (
        <div className="mt-3 pt-3 border-t border-zinc-800 bg-[#0d0d10] -mx-5 -mb-5 px-5 pb-5 rounded-b-lg" data-testid={`coach-note-${s.id}`}>
          <p className="text-[10px] uppercase tracking-wider text-[#C0A062] font-semibold mb-1">Note from coach</p>
          <p className="text-sm text-zinc-300 whitespace-pre-line break-words">{s.coach_note}</p>
        </div>
      )}
    </div>
  );
};

const MySessions = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pending');
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getClientSessions();
      setSessions(res.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load sessions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 12000);
    return () => clearInterval(id);
  }, [load]);

  const grouped = useMemo(() => {
    const pending = [];
    const upcoming = [];
    const past = [];
    for (const s of sessions) {
      if (s.status === 'pending_trainer_response') pending.push(s);
      else if (s.status === 'confirmed' && !isPast(s)) upcoming.push(s);
      else past.push(s);
    }
    return { pending, upcoming, past };
  }, [sessions]);

  const handleCancel = async (s) => {
    setBusy(s.id);
    try {
      const res = await cancelSession(s.id, 'client_cancelled');
      if (res.data?.refunded) toast.success('Cancelled, full refund issued');
      else toast.message('Cancelled');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not cancel');
    } finally {
      setBusy(null);
    }
  };

  const handleLeaveReview = (s) => {
    navigate(`/trainers/${s.trainer_id}#reviews`);
  };

  const tabs = [
    { key: 'pending', label: `Pending (${grouped.pending.length})`, list: grouped.pending },
    { key: 'upcoming', label: `Upcoming (${grouped.upcoming.length})`, list: grouped.upcoming },
    { key: 'past', label: `Past (${grouped.past.length})`, list: grouped.past },
  ];

  const active = tabs.find((t) => t.key === tab);

  return (
    <div className="min-h-screen bg-[#09090B] py-8 px-4 sm:px-6 lg:px-8" data-testid="my-sessions-page">
      <div className="max-w-5xl mx-auto">
        <header className="mb-6 flex items-end justify-between gap-3 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-[#C0A062] font-semibold mb-2">My Sessions</p>
            <h1 className="text-3xl font-bold text-white">Your bookings</h1>
          </div>
          <Button
            onClick={() => navigate('/trainers')}
            className="bg-[#C0A062] text-black hover:bg-[#a98a52]"
            data-testid="find-trainer-btn"
          >
            Find a trainer
          </Button>
        </header>

        <div className="inline-flex rounded-md border border-zinc-800 bg-[#18181B] p-1 mb-6">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-sm rounded ${
                tab === t.key ? 'bg-[#C0A062] text-black font-semibold' : 'text-zinc-400 hover:text-white'
              }`}
              data-testid={`tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <MyPackagesPanel />

        {/* Live trainer tracking: show for any upcoming session whose trainer is en route */}
        {tab === 'upcoming' && grouped.upcoming.filter((s) => s.trainer_en_route).map((s) => (
          <div key={`track-${s.id}`} className="mb-6">
            <ClientTrackingMap session={s} onArrived={load} />
          </div>
        ))}

        {loading ? (
          <div className="text-zinc-500 text-sm py-12 text-center"><Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Loading&hellip;</div>
        ) : active.list.length === 0 ? (
          <div className="bg-[#18181B] border border-zinc-800 rounded-lg py-16 text-center" data-testid={`tab-empty-${tab}`}>
            <Inbox className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-400 text-sm">
              {tab === 'pending' && 'No pending requests right now.'}
              {tab === 'upcoming' && 'No upcoming sessions. Find a trainer to book one.'}
              {tab === 'past' && 'No past sessions yet.'}
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {active.list.map((s) => (
              <SessionCard
                key={s.id}
                s={s}
                onCancel={handleCancel}
                onLeaveReview={handleLeaveReview}
                busy={busy}
              />
            ))}
          </div>
        )}

        {tab === 'upcoming' && grouped.upcoming.length > 0 && (
          <p className="text-[11px] text-zinc-500 mt-6 max-w-md flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
            Cancellation policy: free cancellation up to 24 hours before the session. Within 24 hours: no refund.
          </p>
        )}
      </div>
    </div>
  );
};

export default MySessions;
